import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  UserCheck,
  Stethoscope,
  CheckCircle2,
  Calendar,
  HelpCircle,
  Brain,
  Award,
} from "lucide-react";

export default function PsychiatristVsPsychologistPage() {
  const publishedDate = "2024-09-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/09/is-it-better-to-see-a-psychiatrist-or-psychologist.jpg";

  const faqs = [
    {
      q: "What is the main difference between a psychiatrist and a psychologist?",
      a: "A psychiatrist is a medical doctor (MD/DO) who can diagnose medical mental health conditions, order lab tests, and prescribe medications or interventional procedures (like TMS or Ketamine). A psychologist holds a doctorate (PhD/PsyD) and focuses on non-medical psychotherapy and psychological testing.",
    },
    {
      q: "Should I see a psychiatrist or a psychologist first?",
      a: "If symptoms severely disrupt daily functioning, sleep, or involve chemical imbalances (severe depression, bipolar disorder, psychosis), see a psychiatrist first. If you are seeking talk therapy for life transitions, coping skills, or milder anxiety, start with a psychologist.",
    },
    {
      q: "Can I see both a psychiatrist and a psychologist at the same time?",
      a: "Yes! Collaborative care combining psychiatric medical management or interventional TMS/Ketamine with ongoing psychotherapy provides the highest clinical success rate.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Is It Better to See a Psychiatrist or Psychologist? | Neuro Wellness"
        description="Understand the differences between psychiatrists (MDs prescribing medication & TMS) and psychologists (PhDs providing psychotherapy), and decide which is right for you."
        canonical="/is-it-better-to-see-a-psychiatrist-or-psychologist"
        keywords="psychiatrist vs psychologist, should I see a psychiatrist, psychologist talk therapy, mental health specialist Coral Springs FL"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/is-it-better-to-see-a-psychiatrist-or-psychologist#webpage",
                name: "Is It Better to See a Psychiatrist or Psychologist?",
                url: "https://neurowellnesstms.com/is-it-better-to-see-a-psychiatrist-or-psychologist",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Comprehensive comparison guide detailing medical credentials, prescribing authority, therapy methods, and decision frameworks for psychiatrists vs psychologists.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/is-it-better-to-see-a-psychiatrist-or-psychologist#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <UserCheck className="h-4 w-4" />
                    Mental Health Navigation
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Sept 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  Is It Better to See a{" "}
                  <span className="text-accent">
                    Psychiatrist or Psychologist?
                  </span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Deciding between medical psychiatric intervention and
                  psychological talk therapy—and how collaborative care produces
                  optimal outcomes.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Free Care Guidance
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Need Help Choosing Care?
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLINICAL DIRECTOR QUOTE */}
        <section className="bg-muted/30 border-y border-border py-8">
          <div className="container-px mx-auto max-w-4xl">
            <blockquote className="text-base sm:text-lg italic text-foreground/90 border-l-4 border-accent pl-4 py-1">
              “Therapy is different than psychiatric care, in that therapy is
              not always sought after when there is something amiss. Therapy is
              also indicated for life transitions, adjustments, or during
              periods of growth where you can greatly benefit from a licensed
              and experienced professional who can help you 'peel the onion' and
              find or stay on the right path.”
            </blockquote>
            <p className="mt-2 text-xs sm:text-sm font-semibold text-accent uppercase tracking-wider pl-5">
              — Roxana Carmenate, LCSW-QS, Clinical Director & Lead Therapist At
              Neuro Wellness TMS Centers of America
            </p>
          </div>
        </section>

        {/* BREADCRUMBS & ARTICLE */}
        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "Psychiatrist or Psychologist",
                  href: "/is-it-better-to-see-a-psychiatrist-or-psychologist",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                Many individuals struggling with emotional distress, insomnia,
                or mood shifts wonder whether they should consult a psychiatrist
                or a psychologist. While both disciplines aim to heal
                psychological suffering, their training, treatment tools, and
                clinical focus differ significantly.
              </p>

              <h2>Understanding the Role of a Psychiatrist</h2>
              <p>
                A <strong>psychiatrist</strong> is a licensed Medical Doctor
                (MD) or Doctor of Osteopathic Medicine (DO) who completed
                medical school followed by 4 years of psychiatric residency.
                They evaluate mental illness from a biological and medical
                standpoint, analyzing neurotransmitter levels, brain circuitry,
                physical medical conditions, and medication interactions.
              </p>
              <ul>
                <li>
                  <strong>Prescribing Authority:</strong> Psychiatrists can
                  prescribe psychiatric medications, manage prescriptions, and
                  order lab tests.
                </li>
                <li>
                  <strong>Interventional Psychiatry:</strong> Psychiatrists
                  administer non-pharmacological procedures like{" "}
                  <Link
                    to="/what-is-tms-therapy"
                    className="text-primary hover:underline font-semibold"
                  >
                    TMS therapy
                  </Link>{" "}
                  and IV Ketamine infusions.
                </li>
              </ul>

              <h2>Understanding the Role of a Psychologist</h2>
              <p>
                A <strong>psychologist</strong> holds a doctoral degree in
                psychology (PhD or PsyD) and specializes in psychological
                testing, behavioral modification, and evidence-based talk
                therapies (such as CBT, DBT, or psychodynamic therapy).
              </p>
              <ul>
                <li>
                  <strong>Therapeutic Mastery:</strong> They help individuals
                  identify unhelpful thought patterns, process emotional trauma,
                  and develop coping strategies.
                </li>
                <li>
                  <strong>Psychological Assessments:</strong> Psychologists
                  conduct standardized neuro-cognitive and personality testing.
                </li>
              </ul>

              <h2>Determining Your Mental Health Needs</h2>
              <p>
                Consider seeing a <strong>psychiatrist</strong> if your symptoms
                include severe depression, suicidal ideation, bipolar mood
                swings, severe anxiety attacks, or if medication/TMS is desired.
              </p>
              <p>
                Consider seeing a <strong>psychologist</strong> if you want
                non-medical talk therapy to manage stress, relationship
                conflict, grief, or personal growth.
              </p>
            </article>

            {/* FAQ SECTION */}
            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RELATED RESOURCES */}
            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related Care Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link
                    to="/how-to-see-a-psychiatrist"
                    className="hover:underline"
                  >
                    → How to See a Psychiatrist: Step-by-Step
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-to-get-mental-health-help"
                    className="hover:underline"
                  >
                    → How to Get Mental Health Help
                  </Link>
                </li>
                <li>
                  <Link
                    to="/the-benefits-of-psychotherapy-for-managing-anxiety-and-depression"
                    className="hover:underline"
                  >
                    → Benefits of Psychotherapy
                  </Link>
                </li>
                <li>
                  <Link to="/what-is-tms-therapy" className="hover:underline">
                    → What Is TMS Therapy?
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
